import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    formData.delete('phone_alt');

    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setSubmitting(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const res = await fetch('https://readdy.ai/api/form/d9q7iqsnlsngrm4llssg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '超碰在线',
    url: 'https://www.chaopengzx.com',
    description: '超碰在线是领先的免费视频平台，提供海量高清视频资源，极速加载、流畅播放，无需注册即可畅享。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@chaopengzx.com',
      availableLanguage: ['Chinese'],
    },
    sameAs: ['https://weibo.com/chaopengzx', 'https://space.bilibili.com/chaopengzx'],
  };

  const achievements = [
    { value: '500万+', label: '日活跃用户' },
    { value: '12万+', label: '视频资源' },
    { value: '99.9%', label: '服务可用率' },
    { value: '4.9/5', label: '用户评分' },
  ];

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      content: '超碰在线是我用过最好的免费视频平台，资源更新快，画质清晰，最重要的是完全没有烦人的弹窗广告！',
      rating: 5,
    },
    {
      name: '李女士',
      role: '追剧达人',
      content: '每天下班就靠超碰在线追剧了，电视剧分类特别全，连一些冷门好剧都能找到，真的良心平台。',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      content: '不用注册就能看高清视频，宿舍网速不太好也能流畅播放。动漫资源特别丰富，每周新番更新很及时。',
      rating: 5,
    },
    {
      name: '赵女士',
      role: '全职妈妈',
      content: '陪孩子一起看纪录片和动画片，内容健康适合全家人。界面简单好用，老人也能轻松操作。',
      rating: 5,
    },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mt-8 mb-14">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
                关于<span className="text-primary-500">超碰在线</span>
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                超碰在线成立于2020年，是国内领先的免费视频内容平台。我们致力于为全球华语用户提供海量高清视频资源，
                通过先进的技术架构实现极速加载和流畅播放，让每一位用户都能享受最佳的在线观影体验。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {achievements.map((item) => (
                <div key={item.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">{item.value}</div>
                  <div className="text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-lg font-bold text-foreground-950 font-heading mb-3">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每个人都能自由、便捷地享受高质量视频内容。我们相信，好的内容应该被更多人看到，
                  不应该被付费墙和繁琐的注册流程阻挡。超碰在线坚持免费开放，用技术降低观影门槛。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-rocket-line text-xl"></i>
                </div>
                <h2 className="text-lg font-bold text-foreground-950 font-heading mb-3">技术优势</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  采用全球领先的CDN内容分发网络和自研视频编码技术，确保在全国任何地区都能获得极速流畅的播放体验。
                  智能码率自适应功能，根据用户网络状况自动调整画质，告别卡顿。
                </p>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section id="reputation" className="py-14 border-t border-background-200">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">用户口碑</h2>
              <p className="text-foreground-600 text-sm md:text-base">数百万用户的真实评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((item, index) => (
                <div key={index} className="p-5 rounded-xl bg-background-50 border border-background-200 flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-4">&ldquo;{item.content}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm">
                      {item.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="py-14 border-t border-background-200">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">联系我们</h2>
                <p className="text-foreground-600 text-sm md:text-base">有任何问题或建议？欢迎与我们取得联系</p>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-form-alt-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入咨询主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-background-50 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-xl bg-accent-50 border border-accent-200 text-accent-800 text-sm text-center">
                    <i className="ri-check-line mr-1"></i>
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}
              </form>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
                <div className="p-4 rounded-xl bg-background-50 border border-background-200 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-0.5">邮箱</div>
                  <div className="text-sm font-medium text-foreground-900">support@chaopengzx.com</div>
                </div>
                <div className="p-4 rounded-xl bg-background-50 border border-background-200 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-0.5">客服热线</div>
                  <div className="text-sm font-medium text-foreground-900">400-888-9999</div>
                </div>
                <div className="p-4 rounded-xl bg-background-50 border border-background-200 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-2">
                    <i className="ri-time-line"></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-0.5">工作时间</div>
                  <div className="text-sm font-medium text-foreground-900">周一至周日 9:00-22:00</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}